import {
  isAssetReadOnly,
  Media,
  useCurrentAccount,
  useProduct,
  useWorkspaceAsset,
} from '@gripp/shared-logic';
import {
  LoadingIndicator,
  MediaListItem,
  SlideAction,
  useSlideLayoutSafe,
} from '@gripp/shared-ui';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { View } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import { useNavigate } from 'react-router-dom';
import { useAssetRouting } from '../assetRouteContext';
import { AddAssetMediaType, AddFilesLinksSheet } from './addFilesLinksButton';
import { FilesContext, MediaExtended } from './filesContext';
import { filesSharedStyles } from './filesSharedStyles';

type FileRightColumnProps = {
  assetId: string;
  searchKeyword: string;
  selectedCategoryId: string | undefined;
  onAllFilesLoaded: (files: MediaExtended[]) => void;
  onFilteredCountChange?: (count: number) => void;
  issueId?: string;
};

export const FileRightColumn = ({
  assetId,
  issueId,
  searchKeyword,
  selectedCategoryId,
  onAllFilesLoaded,
  onFilteredCountChange,
}: FileRightColumnProps) => {
  const { workspaceAsset, invalidateQuery, isLoading } =
    useWorkspaceAsset(assetId);
  const { basePath } = useAssetRouting(assetId);
  const asset = workspaceAsset?.asset;
  const { account } = useCurrentAccount();
  const isReadOnlyMode = isAssetReadOnly(account, workspaceAsset);
  const navigate = useNavigate();
  const slideLayout = useSlideLayoutSafe();
  const { isAddSheetOpen, setIsAddSheetOpen } = useContext(FilesContext);

  // Picking a type slides into the dedicated `files/new` panel — the sheet no
  // longer opens an upload Modal of its own.
  const handleSelectType = useCallback(
    (type: AddAssetMediaType) => {
      setIsAddSheetOpen(false);
      slideLayout?.prepareSlideTransition(SlideAction.NEXT);
      navigate(`${basePath}/files/new?type=${type}`, {
        state: { slideLayoutInitialTransition: SlideAction.NEXT },
      });
    },
    [basePath, navigate, slideLayout, setIsAddSheetOpen]
  );

  // Render the add sheet as a column overlay so its backdrop covers the
  // PanelHeader too (the inline sheet only darkens the body area). The
  // overlay is only registered while actually needed — otherwise the
  // columnOverlay wrapper would capture clicks on the right column.
  useEffect(() => {
    if (!slideLayout || !asset || isReadOnlyMode) return;
    if (!isAddSheetOpen) {
      slideLayout.setColumnOverlay(null);
      return;
    }
    slideLayout.setColumnOverlay(
      <AddFilesLinksSheet
        isOpen={isAddSheetOpen}
        onClose={() => setIsAddSheetOpen(false)}
        onSelectType={handleSelectType}
      />
    );
    return () => slideLayout.setColumnOverlay(null);
  }, [
    slideLayout,
    asset,
    isReadOnlyMode,
    isAddSheetOpen,
    setIsAddSheetOpen,
    handleSelectType,
  ]);

  const { product } = useProduct(asset?.product?.id || '');

  const allFiles = useMemo(() => {
    const files: MediaExtended[] = [];

    const productMediaIds = new Set(
      product?.media?.map((m: Media) => m.id) || []
    );

    if (asset?.media) {
      asset.media.forEach((media: Media) => {
        const isFromProduct = productMediaIds.has(media.id);
        files.push({ ...media, isFromProduct });
      });
    }

    onAllFilesLoaded(files);
    return files;
  }, [asset?.media, product?.media, onAllFilesLoaded]);

  const filteredFiles = useMemo(() => {
    let files = allFiles;

    if (selectedCategoryId) {
      files = files.filter((file) =>
        file.categories?.some((cat) => cat.id === selectedCategoryId)
      );
    }

    if (searchKeyword.trim()) {
      const query = searchKeyword.toLowerCase();
      files = files.filter((file) => file.name?.toLowerCase().includes(query));
    }

    return files;
  }, [allFiles, searchKeyword, selectedCategoryId]);

  useEffect(() => {
    onFilteredCountChange?.(filteredFiles.length);
  }, [filteredFiles.length, onFilteredCountChange]);

  const handleMediaClick = useCallback(
    (media: Media) => {
      const url = issueId
        ? `/activities/${issueId}/asset/${assetId}/files/${media.id}`
        : `${basePath}/files/${media.id}`;

      // Pass source so AssetFilePreview knows to show the file list in the
      // left panel (current panel stays as the navigation context).
      slideLayout?.prepareSlideTransition(SlideAction.NEXT);
      navigate(url, {
        state: {
          source: 'filesList',
          slideLayoutInitialTransition: SlideAction.NEXT,
        },
      });
    },
    [assetId, basePath, issueId, navigate, slideLayout]
  );

  if (isLoading) {
    return (
      <View style={filesSharedStyles.container}>
        <LoadingIndicator flexContainer />
      </View>
    );
  }

  if (!workspaceAsset) return null;

  return (
    <View style={filesSharedStyles.container}>
      <ScrollView
        showsVerticalScrollIndicator={true}
        style={filesSharedStyles.scrollView}
      >
        {filteredFiles.map((file: MediaExtended) => (
          <MediaListItem
            key={file.id}
            media={file}
            invalidateQuery={invalidateQuery}
            useNewCropCircle
            onClickMedia={handleMediaClick}
          />
        ))}
      </ScrollView>
    </View>
  );
};
